import React, { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Maximize2Icon } from "lucide-react";
import { ALBUMS, GALLERY } from "../../data/gallery";
import type { Album, GalleryItem } from "../../data/gallery";
import { EASE } from "../../utils/motion";
type AlbumGridProps = {
  onOpen: (items: GalleryItem[], id: string) => void;
};
type Filter = "All" | Album;
const SHAPE: Record<GalleryItem["shape"], string> = {
  tall: "row-span-2",
  wide: "col-span-2",
  square: ""
};
export function AlbumGrid({
  onOpen
}: AlbumGridProps) {
  const [filter, setFilter] = useState<Filter>("All");
  const reduce = useReducedMotion();
  const items = filter === "All" ? GALLERY : GALLERY.filter(g => g.album === filter);
  const filters: Filter[] = ["All", ...ALBUMS];
  return <section className="mx-auto max-w-[1440px] px-5 py-24 md:px-9 md:py-32" data-id="element-172" data-mp="37hsd1q1zufyu">
      <div className="flex flex-col justify-between gap-8 border-b border-[#061f38] pb-8 lg:flex-row lg:items-end" data-id="element-173" data-mp="32ifuvim2hx2l">
        <div data-id="element-174" data-mp="1o1ejjn94sfzt">
          <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-175" data-mp="1o1wor9dru4jv">BROWSE THE ALBUMS</p>
          <h2 className="mt-5 text-5xl font-medium leading-[0.92] tracking-[-0.07em] md:text-7xl" data-id="element-176" data-mp="1szy5hnnq5rs6">Find your view.</h2>
        </div>
        <div role="tablist" aria-label="Gallery albums" className="no-scrollbar -mx-5 flex gap-1 overflow-x-auto px-5 lg:mx-0 lg:px-0" data-id="element-177" data-mp="1xwol69vmald2">
          {filters.map(f => {
          const count = f === "All" ? GALLERY.length : GALLERY.filter(g => g.album === f).length;
          return <button key={f} role="tab" aria-selected={filter === f} onClick={() => setFilter(f)} className={`relative shrink-0 whitespace-nowrap px-4 py-2.5 text-sm transition-colors duration-200 ${filter === f ? "text-white" : "text-[#4f5963] hover:text-[#061f38]"}`} data-id="element-178" data-mp="2brr2dt5wzx56">
                {filter === f && <motion.span layoutId="album-filter" transition={{
              duration: reduce ? 0 : 0.4,
              ease: EASE
            }} className="absolute inset-0 bg-[#061f38]" data-id="element-179" data-mp="md2qru55ej87" />}
                <span className="relative" data-id="element-180" data-mp="jag2e039mqhr">{f} <sup className="ml-0.5 text-[10px] opacity-60" data-id="element-181" data-mp="j9n4lomabbbr">{count}</sup></span>
              </button>;
        })}
        </div>
      </div>

      <motion.div layout className="mt-10 grid auto-rows-[160px] grid-flow-dense grid-cols-2 gap-3 md:auto-rows-[220px] lg:grid-cols-4" data-id="element-182" data-mp="t83zuxs9ac3m">
        <AnimatePresence mode="popLayout" data-id="element-183" data-mp="o6ws2yq45cwy">
          {items.map((item, i) => <motion.button layout key={item.id} type="button" onClick={() => onOpen(items, item.id)} initial={{
          opacity: 0,
          scale: reduce ? 1 : 0.94
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: reduce ? 1 : 0.94
        }} transition={{
          duration: reduce ? 0.01 : 0.5,
          delay: reduce ? 0 : Math.min(i * 0.03, 0.3),
          ease: EASE
        }} className={`group relative overflow-hidden bg-[#dedbd3] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bb1e35] focus-visible:ring-offset-2 ${SHAPE[item.shape]}`} aria-label={`Open ${item.caption}`} data-id="element-184" data-mp="1277ki672mzk8" data-mpi={i}>
              <img src={item.src} alt={item.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.07]" data-id="element-185" data-mp="24k0kw7n4mydx" />
              <span className="absolute inset-0 bg-[#061f38]/0 transition-colors duration-300 group-hover:bg-[#061f38]/45" data-id="element-186" data-mp="1visx4ji8lfy3" />
              <span className="absolute inset-x-0 bottom-0 flex translate-y-3 items-end justify-between gap-3 p-4 text-white opacity-0 transition-[opacity,transform] duration-300 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100" data-id="element-187" data-mp="1gteoxeqed4ka">
                <span data-id="element-188" data-mp="1bw4n0zltn0fk">
                  <span className="block text-[10px] font-semibold tracking-[0.16em] text-[#f0b1b9]" data-id="element-189" data-mp="1btilbglm3f59">{item.album.toUpperCase()}</span>
                  <span className="mt-1 block text-base font-medium tracking-[-0.02em]" data-id="element-190" data-mp="12vgbcbjwhc5u">{item.caption}</span>
                </span>
                <Maximize2Icon size={16} className="shrink-0" data-id="element-191" data-mp="25834gmbj3b8d" />
              </span>
            </motion.button>)}
        </AnimatePresence>
      </motion.div>
      <p className="mt-6 text-xs text-[#6b7378]" data-id="element-192" data-mp="1wfhmh8fq18kl">Photography shown is visual direction. Authentic MCC albums will be published through the CMS.</p>
    </section>;
}